// Автоматические подписи для кнопок-иконок без текста (скринридеры и подсказка при наведении).
// В интерфейсе сотни кнопок с одной иконкой (изменить, удалить, закрыть…). Вместо ручной правки каждой подпись выводится
// из самой иконки; явные aria-label/title и кнопки с текстом не трогаются.
import { appLang } from './date';

const LABELS = {
  ru: { edit: 'Изменить', delete: 'Удалить', add: 'Добавить', close: 'Закрыть', prev: 'Назад', next: 'Далее', call: 'Позвонить', search: 'Поиск', more: 'Ещё', archive: 'В архив', copy: 'Копировать', download: 'Скачать', upload: 'Загрузить', refresh: 'Обновить', settings: 'Настройки', send: 'Отправить', show: 'Показать', hide: 'Скрыть', move: 'Переместить', select: 'Выбрать' },
  uz: { edit: 'Tahrirlash', delete: "O'chirish", add: "Qo'shish", close: 'Yopish', prev: 'Orqaga', next: 'Keyingi', call: "Qo'ng'iroq qilish", search: 'Qidiruv', more: 'Yana', archive: 'Arxivga', copy: 'Nusxalash', download: 'Yuklab olish', upload: 'Yuklash', refresh: 'Yangilash', settings: 'Sozlamalar', send: 'Yuborish', show: "Ko'rsatish", hide: 'Yashirish', move: "Ko'chirish", select: 'Tanlash' },
  tr: { edit: 'Düzenle', delete: 'Sil', add: 'Ekle', close: 'Kapat', prev: 'Geri', next: 'İleri', call: 'Ara', search: 'Ara', more: 'Daha fazla', archive: 'Arşivle', copy: 'Kopyala', download: 'İndir', upload: 'Yükle', refresh: 'Yenile', settings: 'Ayarlar', send: 'Gönder', show: 'Göster', hide: 'Gizle', move: 'Taşı', select: 'Seç' },
  en: { edit: 'Edit', delete: 'Delete', add: 'Add', close: 'Close', prev: 'Back', next: 'Next', call: 'Call', search: 'Search', more: 'More', archive: 'Archive', copy: 'Copy', download: 'Download', upload: 'Upload', refresh: 'Refresh', settings: 'Settings', send: 'Send', show: 'Show', hide: 'Hide', move: 'Move', select: 'Select' },
};

// имя иконки lucide → ключ подписи
const ICON = {
  pencil: 'edit', pen: 'edit', 'square-pen': 'edit', edit: 'edit',
  trash: 'delete', 'trash-2': 'delete',
  plus: 'add', x: 'close',
  'chevron-left': 'prev', 'arrow-left': 'prev', 'chevron-right': 'next', 'arrow-right': 'next',
  phone: 'call', 'phone-call': 'call', search: 'search',
  'more-horizontal': 'more', 'more-vertical': 'more', ellipsis: 'more', 'ellipsis-vertical': 'more',
  archive: 'archive', copy: 'copy', download: 'download', upload: 'upload',
  'refresh-cw': 'refresh', 'rotate-ccw': 'refresh', settings: 'settings', send: 'send',
  eye: 'show', 'eye-off': 'hide', layers: 'move',
};

function nameOf(btn) {
  const svg = btn.querySelector('svg');
  if (svg) {
    const names = [...svg.classList].filter(c => c.startsWith('lucide-')).map(c => c.slice(7));
    for (const n of names) if (ICON[n]) return ICON[n];
    return null;
  }
  // Пустая кнопка без иконки внутри таблицы: это чекбокс выбора строки
  if (!btn.textContent.trim() && (btn.closest('td, th') || /\brounded\b.*\bborder\b/.test(String(btn.className)))) return 'select';
  return null;
}

let lastLang = null;
function label(btn, lang) {
  if (btn.getAttribute('aria-label') && !btn.hasAttribute('data-a11y')) return;     // подпись задана вручную
  if (btn.getAttribute('title') && !btn.hasAttribute('data-a11y')) return;
  if (btn.textContent.trim() && !btn.hasAttribute('data-a11y')) return;
  const key = nameOf(btn);
  if (!key) return;
  const text = (LABELS[lang] || LABELS.ru)[key];
  btn.setAttribute('aria-label', text);
  btn.setAttribute('title', text);
  btn.setAttribute('data-a11y', '1');
}

// Выпадающие списки без подписи (фильтры «Все сотрудники», «Все воронки»): имя берём из первого пункта, иначе из подписи рядом
function labelSelect(sel) {
  if (sel.getAttribute('aria-label') || sel.getAttribute('aria-labelledby') || sel.id && document.querySelector('label[for="' + sel.id + '"]') || sel.closest('label')) return;
  const first = sel.options && sel.options[0] ? sel.options[0].textContent.trim() : '';
  const near = sel.previousElementSibling && /^(LABEL|SPAN|P)$/.test(sel.previousElementSibling.tagName) ? sel.previousElementSibling.textContent.trim() : '';
  const text = near || first;
  if (text) sel.setAttribute('aria-label', text);
}

function scan(root, lang) {
  if (!root || root.nodeType !== 1) return;
  if (root.tagName === 'BUTTON') label(root, lang);
  if (root.tagName === 'SELECT') labelSelect(root);
  root.querySelectorAll?.('button').forEach(b => label(b, lang));
  root.querySelectorAll?.('select').forEach(labelSelect);
}

export function initA11y() {
  if (typeof window === 'undefined' || window.__a11yInit) return;
  window.__a11yInit = true;
  let queued = false; const pending = new Set();
  const flush = () => {
    queued = false;
    const lang = appLang();
    if (lang !== lastLang) { lastLang = lang; scan(document.body, lang); pending.clear(); return; }   // язык сменился: перерасчёт всех
    pending.forEach(n => scan(n, lang)); pending.clear();
  };
  const obs = new MutationObserver((muts) => {
    muts.forEach(m => m.addedNodes.forEach(n => { if (n.nodeType === 1) pending.add(n); }));
    if (!queued) { queued = true; requestAnimationFrame(flush); }
  });
  obs.observe(document.body, { childList: true, subtree: true });
  flush();
}
